import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Search, Shield } from 'lucide-react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';

export default function ClubsPage() {
  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editClub, setEditClub] = useState(null);
  const [form, setForm] = useState({ name: '', short_name: '', city: '', badge_url: '', stadium: '' });
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Bulk delete
  const [bulkDeleteEnabled, setBulkDeleteEnabled] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [stadiums, setStadiums] = useState([]);

  const fetchClubs = () => {
    setLoading(true);
    api.get('/api/clubs').then(r => setClubs(r.data)).catch(() => toast.error('Failed to load clubs')).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchClubs();
    api.get('/api/stadiums').then(r => setStadiums(r.data)).catch(() => {});
    if (hasPerm('delete_clubs')) {
      api.get('/api/settings').then(r => setBulkDeleteEnabled(!!r.data.allow_bulk_delete)).catch(() => {});
    }
  }, []);

  const handleOpen = (club = null) => {
    if (club) {
      setEditClub(club);
      setForm({ name: club.name, short_name: club.short_name || '', city: club.city || '', badge_url: club.badge_url || '', stadium: club.stadium || '' });
    } else {
      setEditClub(null);
      setForm({ name: '', short_name: '', city: '', badge_url: '', stadium: '' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t('fill_required')); return; }
    setSaving(true);
    try {
      if (editClub) {
        await api.put(`/api/clubs/${editClub.id}`, form);
        toast.success(t('club_updated'));
      } else {
        await api.post('/api/clubs', form);
        toast.success(t('club_created'));
      }
      setOpen(false);
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editClub) return;
    setSaving(true);
    try {
      await api.delete(`/api/clubs/${editClub.id}`);
      toast.success(t('club_deleted'));
      setOpen(false);
      fetchClubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); setConfirmOpen(false); }
  };

  const filtered = clubs.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || (c.city || '').toLowerCase().includes(q) || (c.short_name || '').toLowerCase().includes(q);
  });

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    const ids = filtered.map(c => c.id);
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post('/api/clubs/bulk-delete', { ids: [...selected] });
      const { deleted, skipped } = r.data;
      if (deleted > 0) toast.success(`Deleted ${deleted} clubs`);
      if (skipped?.length > 0) {
        toast.warning(`${skipped.length} club(s) skipped — used in matches: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? '...' : ''}`);
      }
      setSelected(new Set());
      fetchClubs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBulkDeleting(false);
    setBulkConfirmOpen(false);
  };

  return (
    <div data-testid="clubs-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('clubs')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{clubs.length} {t('clubs')}</p>
        </div>
        <div className="flex gap-2">
          {hasPerm('create_clubs') && (
          <Button data-testid="add-club-btn" onClick={() => handleOpen()} className="bg-gray-900 hover:bg-gray-800 text-sm h-9">
            <Plus size={15} className="mr-1.5" /> {t('add_club')}
          </Button>
          )}
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="club-search" placeholder={`${t('search')}...`} value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-white border-gray-300 h-10 text-sm" />
      </div>

      {/* Bulk delete bar */}
      {bulkDeleteEnabled && hasPerm('delete_clubs') && selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
          <span className="text-sm font-medium text-red-700 dark:text-red-400">{selected.size} selected</span>
          <Button data-testid="bulk-delete-clubs-btn" size="sm" variant="destructive" onClick={() => setBulkConfirmOpen(true)} className="h-8 text-xs">
            <Trash2 size={13} className="mr-1" /> Delete Selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="h-8 text-xs text-gray-500">Clear</Button>
          <ConfirmDialog open={bulkConfirmOpen} onCancel={() => setBulkConfirmOpen(false)} onConfirm={handleBulkDelete}
            title="Delete Clubs" description={`Are you sure you want to delete ${selected.size} clubs? This cannot be undone.`} confirmText={bulkDeleting ? 'Deleting...' : 'Delete'} destructive />
        </div>
      )}

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100 hover:bg-transparent">
                {bulkDeleteEnabled && hasPerm('delete_clubs') && (
                  <TableHead className="w-10 text-center">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300"
                      checked={filtered.length > 0 && selected.size === filtered.length}
                      onChange={toggleSelectAll} />
                  </TableHead>
                )}
                <TableHead className="text-xs text-gray-400 font-medium w-12"></TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('club_name')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('short_name')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('city')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">{t('stadium')}</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={bulkDeleteEnabled ? 7 : 6} className="text-center py-8 text-gray-400">{t('loading')}</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={bulkDeleteEnabled ? 7 : 6} className="text-center py-8 text-gray-400">{t('no_clubs')}</TableCell></TableRow>
              ) : filtered.map(club => (
                <TableRow key={club.id} className="border-gray-100 hover:bg-gray-50/70 h-12 group" data-testid={`club-row-${club.id}`}>
                  {bulkDeleteEnabled && hasPerm('delete_clubs') && (
                    <TableCell className="py-2 text-center">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300"
                        checked={selected.has(club.id)}
                        onChange={() => toggleSelect(club.id)} />
                    </TableCell>
                  )}
                  <TableCell className="py-2">
                    {club.badge_url ? (
                      <img src={club.badge_url} alt={club.name} className="h-8 w-8 object-contain" />
                    ) : (
                      <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                        <Shield size={16} className="text-gray-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-semibold text-gray-800 text-sm">
                    {club.name}
                    {club.status === 'relegated' && (
                      <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-600 bg-amber-50 py-0 px-1.5">Relegated</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-500 text-sm">{club.short_name}</TableCell>
                  <TableCell className="text-gray-500 text-sm">{club.city}</TableCell>
                  <TableCell className="text-gray-400 text-xs max-w-[200px] truncate">{club.stadium}</TableCell>
                  <TableCell className="py-2 text-center">
                    {hasPerm('edit_clubs') && (
                    <Button
                      data-testid={`edit-club-${club.id}`}
                      variant="ghost" size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 text-gray-400 hover:text-primary"
                      onClick={() => handleOpen(club)}
                    >
                      <Pencil size={14} />
                    </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {editClub ? t('edit_club') : t('add_club')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm text-gray-700">{t('club_name')} *</Label>
              <Input data-testid="club-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-gray-50 border-gray-300 h-10 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-700">{t('short_name')}</Label>
                <Input data-testid="club-short-input" value={form.short_name} onChange={e => setForm(p => ({...p, short_name: e.target.value}))} className="bg-gray-50 border-gray-300 h-10 text-sm" placeholder="e.g. HIL" />
              </div>
              <div>
                <Label className="text-sm text-gray-700">{t('city')}</Label>
                <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} className="bg-gray-50 border-gray-300 h-10 text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-gray-700">{t('stadium')}</Label>
              <Select value={form.stadium || '_none'} onValueChange={v => setForm(p => ({...p, stadium: v === '_none' ? '' : v}))}>
                <SelectTrigger className="bg-gray-50 border-gray-300 h-10 text-sm"><SelectValue placeholder="Select stadium" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-60">
                  <SelectItem value="_none">— None —</SelectItem>
                  {stadiums.map(s => <SelectItem key={s.id} value={s.name}>{s.name}{s.city ? ` (${s.city})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-700">{t('badge_url')}</Label>
              <Input value={form.badge_url} onChange={e => setForm(p => ({...p, badge_url: e.target.value}))} className="bg-gray-50 border-gray-300 h-10 text-sm" placeholder="https://..." />
              {form.badge_url && <img src={form.badge_url} alt="" className="h-10 w-10 object-contain mt-2" />}
            </div>
          </div>
          <DialogFooter className="flex gap-2 pt-2">
            {editClub && (
              <Button data-testid="delete-club-btn" variant="outline" onClick={() => setConfirmOpen(true)} disabled={saving} className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 size={14} className="mr-1.5" /> {t('delete')}
              </Button>
            )}
            <ConfirmDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)} onConfirm={handleDelete}
              title="Delete Club" description={`Are you sure you want to delete ${editClub?.name}?`} confirmText="Delete" destructive />
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} className="border-gray-300">{t('cancel')}</Button>
            <Button data-testid="save-club-btn" onClick={handleSave} disabled={saving} className="bg-gray-900 hover:bg-gray-800 text-sm">
              {saving ? '...' : (editClub ? t('save_changes') : t('add_club'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
